/* eslint-disable react/jsx-boolean-value */
import {
  Dispatch, SetStateAction, Key, RefObject, useState, useEffect,
} from 'react';
import {
  Row, Col, Icons, Button, Popover, Form, Select, Radio, Badge, Divider, Input, Typography, Collapse,
} from 'taxaroo-ui';
import styled from 'styled-components';
import ActionButton from '~src/components/molecules/ActionButton';
import SelectJobType from '~src/components/atoms/SelectJobType';
import { useAppSelector } from '~src/redux/hooks';
import { useGetUserFirmAccountByFirmAccountAndFilterQuery } from '~src/graphql/queries/settings';
import { ClientsFilterInput, TaxYearStatus } from '~src/graphql';
import ExportButton from './ExportButton';
import SearchBar from './SearchBar';
import SelectNumJobsDropdown from './SelectNumJobsDropdown';
import SendTextMessageButton from './SendTextMessageButton';

const { FilterOutlined } = Icons;
const { Option } = Select;
// fill tax year list
const currentYear = (new Date()).getFullYear();
const taxYears: Array<number> = [(currentYear - 1), currentYear];
for (let year = currentYear - 2; year > currentYear - 10; year -= 1) {
  taxYears.push(year);
}
//
const paymentStatusOptions = [
  { value: 'completed', label: 'Completed', key: 'completed_status_payment' },
  { value: 'client_updated', label: 'Client Updated Info', key: 'client_updated_status_payment' },
  { value: 'waiting_for_client', label: 'Waiting for client payment', key: 'waiting_for_client_status_payment' },
  { value: 'payments_not_found', label: 'Not started', key: 'not_found_status_payment' },
];
//
const documentStatusOptions = [
  { value: 'completed', label: 'Completed', key: 'completed_status_document' },
  { value: 'client_updated', label: 'Client Updated Info', key: 'client_updated_status_document' },
  { value: 'waiting_for_client', label: 'Waiting for client response', key: 'waiting_for_client_status_document' },
  { value: 'documents_not_found', label: 'No pending documents', key: 'not_found_status_document' },
];
//
const notesOptions = [
  { value: true, label: 'Yes', key: 'yes_notes' },
  { value: false, label: 'No', key: 'no_notes' },
];
//
const activationStatusOptions = [
  { value: 'sent_accepted', label: 'Sent and accepted', key: 'sent_accepted_status_email' },
  { value: 'sent_not_accepted', label: 'Sent and not accepted', key: 'client_updated_status_email' },
  { value: 'not_sent', label: 'Not sent', key: 'not_sent_status_email' },
  { value: 'active_client', label: 'Active client', key: 'active_client_status_email' },
];

const CollapseFilterComponent = styled(Collapse)`
  .ant-collapse-item > .ant-collapse-header {
    padding: 0 0 20px 0;
  }

  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0;
  }
`;
//
interface RowAboveJobsTableProps {
  selectedRows: unknown[];
  setSelectedRows: Dispatch<SetStateAction<never[]>>;
  setSelectedKeys: Dispatch<SetStateAction<Key[]>>;
  tableData: unknown[];
  tableRef: RefObject<HTMLDivElement>;
  type?: 'clients' | 'jobs';
  displayClearbutton: boolean;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  setSearchedValue: (value: string) => void,
  searchRef: RefObject<HTMLInputElement>,
  loading: boolean;
  progressStatusName?: string;
  setFilters: (value: ClientsFilterInput) => void;
  underlyingStatus?: TaxYearStatus;
}

const RowAboveJobsTable = ({
  selectedRows,
  setSelectedRows,
  setSelectedKeys,
  tableData,
  tableRef,
  type,
  displayClearbutton,
  pageSize,
  setPageSize,
  setSearchedValue,
  searchRef,
  loading,
  progressStatusName,
  setFilters,
  underlyingStatus,
}: RowAboveJobsTableProps) => {
  const [searchBarValue, setSearchBarValue] = useState('');
  const [sendTextDisabled, setSendTextDisabled] = useState(true);
  const [filterType, setFilterType] = useState<string>('AND');
  const [activeFilters, setActiveFilters] = useState(0);
  const [form] = Form.useForm();
  const { firmAccountId } = useAppSelector((state) => state.session);
  const { data, loading: loadingMembers } = useGetUserFirmAccountByFirmAccountAndFilterQuery({
    variables: {
      firmAccountId,
      filterDelete: true,
    },
  });

  const userFirmAccounts = data?.UserFirmAccountByFirmAccountAndFilter;

  useEffect(() => {
    setActiveFilters(Object.values(form.getFieldsValue()).filter((item: string[] | number[] | undefined) => item && item.length > 0).length);
  }, [form.getFieldsValue()]);

  useEffect(() => {
    if (selectedRows.length > 0) {
      const now = new Date().getHours();
      if (now >= 20 || now < 9) {
        setSendTextDisabled(true);
      } else {
        setSendTextDisabled(false);
      }
    } else {
      setSendTextDisabled(true);
    }
  }, [selectedRows?.length]);

  const clearTable = () => {
    setSearchedValue('');
    setSearchBarValue('');
  };

  const onChangeFilters = () => {
    setFilters({ ...form.getFieldsValue(), filterType });
  };

  const onClearFilters = () => {
    form.resetFields();
  };

  const getFilterContent = () => (
    <>
      <Row justify="center" style={{ margin: '5px 0px 15px 0px' }}>
        <Radio.Group value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <Radio.Button value="AND">AND</Radio.Button>
          <Radio.Button value="OR">OR</Radio.Button>
        </Radio.Group>
      </Row>
      {/* <Divider /> */}
      <Form
        form={form}
        layout="horizontal"
        style={{ width: '400px' }}
        initialValues={{ filterType: 'AND' }}
      >
        <Form.Item
          label="Tax Year"
          name="taxYear"
        >
          <Select
            placeholder="Select tax year"
            mode="multiple"
            allowClear
          >
            {taxYears.map((item) => (
              <Option value={item} key={item}>
                {item}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Job Type"
          name="jobType"
        >
          <SelectJobType
            mode="multiple"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="Document Status"
          name="documentStatus"
        >
          <Select
            placeholder="Select a status"
            mode="multiple"
            allowClear
          >
            {documentStatusOptions.map((item) => (
              <Option value={item.value} key={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {[TaxYearStatus.Custom, TaxYearStatus.InProgress].includes(underlyingStatus) ? (
          <Form.Item
            label="Interview Completed"
            name="interviewCompleted"
          >
            <Select
              placeholder="Select an option"
              allowClear
            >
              {[{ label: 'Yes', value: true }, { label: 'No', value: false }].map((item) => (
                <Option value={item.value} key={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        ) : null}
        <Form.Item
          label="Activation Email Status"
          name="activationStatus"
        >
          <Select
            placeholder="Select a status"
            mode="multiple"
            allowClear
          >
            {activationStatusOptions.map((item) => (
              <Option value={item.value} key={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Has Notes"
          name="notes"
        >
          <Select
            placeholder="Select an option"
            allowClear
          >
            {notesOptions.map((item) => (
              <Option value={item.value} key={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Payment Status"
          name="paymentStatus"
        >
          <Select
            placeholder="Select a status"
            mode="multiple"
            allowClear
          >
            {paymentStatusOptions.map((item) => (
              <Option value={item.value} key={item.key}>
                {item.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Preparer"
          name="preparerId"
        >
          <Select
            placeholder="Select a preparer"
            mode="multiple"
            allowClear
          >
            <Option value="Not Assigned">Not Assigned</Option>
            {userFirmAccounts
                && userFirmAccounts.map((item) => (
                  <Option
                    key={item.Users.id}
                    value={item.Users.id}
                    style={item.Users.deleteAt ? { display: 'none' } : {}}
                  >
                    {`${item.Users.UserInformation.firstName} ${item.Users.UserInformation.lastName}`}
                  </Option>
                ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Reviewer"
          name="reviewerId"
        >
          <Select
            placeholder="Select a reviewer"
            mode="multiple"
            allowClear
          >
            <Option value="Not Assigned">Not Assigned</Option>
            {userFirmAccounts
                && userFirmAccounts.map((item) => (
                  <Option
                    key={item.Users.id}
                    value={item.Users.id}
                  >
                    {`${item.Users.UserInformation.firstName} ${item.Users.UserInformation.lastName}`}
                  </Option>
                ))}
          </Select>
        </Form.Item>
        <Divider />
        <CollapseFilterComponent ghost>
          <Collapse.Panel header="Client Basic Info" key="1">
            <Form.Item
              label="First Name"
              name="firstName"
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="lastName"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Email"
              name="email"
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Phone number"
              name="phoneNumber"
            >
              <Input />
            </Form.Item>
          </Collapse.Panel>
        </CollapseFilterComponent>
        <Row
          justify="space-between"
        >
          <ActionButton tooltip={null} onClick={() => onClearFilters()}>
            Clear
          </ActionButton>
          <Button type="primary" onClick={() => onChangeFilters()}>
            Apply
          </Button>
        </Row>
      </Form>
    </>
  );

  const filterElement = (
    <Badge count={activeFilters} size="small">
      <Popover
        content={getFilterContent()}
        placement="bottom"
        trigger="click"
        autoAdjustOverflow
      >
        <FilterOutlined />
      </Popover>
    </Badge>
  );

  return (
    <Row justify="start" gutter={[{ xs: 8, sm: 16, md: 26 }, 16]} style={{ marginTop: '15px' }}>
      <Col>
        <SendTextMessageButton
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          setSelectedKeys={setSelectedKeys}
          sendTextDisabled={sendTextDisabled}
        />
      </Col>
      {tableData && (
        <Col>
          <ExportButton
            dataType={type}
            progressStatusName={progressStatusName}
            tableData={tableData}
            tableRef={tableRef}
          />
        </Col>
      )}
      <Col style={{ flexGrow: 1 }}>
        <SearchBar
          disabled={loading}
          searchRef={searchRef}
          setSearchedValue={setSearchedValue}
          searchBarValue={searchBarValue}
          setSearchBarValue={setSearchBarValue}
          prefix={filterElement}
        />
      </Col>
      {displayClearbutton && (
        <Col>
          <ActionButton
            buttonProps={{ type: 'primary' }}
            tooltip="Clear results"
            onClick={clearTable}
          >
            clear
          </ActionButton>
        </Col>
      )}
      <Col />
      <Col>
        <SelectNumJobsDropdown
          type={type}
          onChange={(value) => setPageSize(value)}
          currentPageSize={pageSize}
        />
      </Col>
    </Row>
  );
};

RowAboveJobsTable.defaultProps = {
  type: 'clients',
  progressStatusName: '',
  underlyingStatus: undefined,
};

export default RowAboveJobsTable;
